import React, { useEffect, useMemo, useState } from "react";
import BodyWrapper from "../../../components/Wrapper/BodyWrapper";
import Header from "../../../components/Header/Header";
import { useDispatch, useSelector } from "react-redux";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import CashPaymentsSummaryHero from "../../../components/CashPayments/CashPaymentsSummaryHero";
import CashTxnCardList from "../../../components/CashPayments/CashTxnCardList";
import { fetchCashBooks } from "../../../store/Slices/CashBookSlice";
import SegmentToggle from "../../../components/Input/SegmentToggle";
import { BsSearch } from "react-icons/bs";
import EditPaymentModal from "../../../components/Modals/EditPaymentModal";
import DeleteModal from "../../../components/Modals/DeleteModal";
import {
  DeleteCustomerPaymentApi,
  DeleteSupplierPaymentApi,
} from "../../../ApiRequests";
import { ErrorToast, SuccessToast } from "../../../utils/ShowToast";
import { toLocalYmd } from "../../../utils/dateTime";

const userData = JSON.parse(localStorage.getItem("user") || "{}");
const storedBranch = localStorage.getItem("branch");
const branchId =
  userData.role === 2
    ? userData.branchId
    : userData.branchId || storedBranch || "";

const TAB_OPTIONS = [
  { value: "in", label: "Cash In" },
  { value: "out", label: "Cash Out" },
];

const paymentTypeFromRow = (row) =>
  row?.source === "company_payment" ? "Company" : "Customer";

const CashPayments = () => {
  const dispatch = useDispatch();
  const CashBookState = useSelector((state) => state.CashBookState);
  const [activeTab, setActiveTab] = useState("in");
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      .toISOString()
      .split("T")[0]
  );
  const [endDate, setEndDate] = useState(
    toLocalYmd()
  );
  const [OpenEditModal, setOpenEditModal] = useState(false);
  const [OpenDeleteModal, setOpenDeleteModal] = useState(false);
  const [Selected, setSelected] = useState(null);
  const [Loading, setLoading] = useState(false);

  const loadCashBook = () => {
    dispatch(
      fetchCashBooks({
        branchId,
        startDate,
        endDate,
      })
    );
  };

  useEffect(() => {
    if (!endDate || endDate < startDate) {
      setEndDate(startDate);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadCashBook();
  }, []);

  const payload = CashBookState.data;
  const summary = useMemo(
    () =>
      payload?.summary || {
        totalIn: 0,
        totalOut: 0,
        balance: 0,
        count: 0,
      },
    [payload]
  );
  const entries = payload?.entries || [];
  const accountName = payload?.account?.account_name || "CASH";

  const tabEntries = useMemo(() => {
    if (activeTab === "in") {
      return entries.filter((row) => Number(row.dr) > 0);
    }
    return entries.filter((row) => Number(row.cr) > 0);
  }, [entries, activeTab]);

  const tabSummary = useMemo(
    () => ({
      count: tabEntries.length,
      total:
        activeTab === "in"
          ? Number(summary.totalIn || 0)
          : Number(summary.totalOut || 0),
    }),
    [tabEntries.length, activeTab, summary.totalIn, summary.totalOut]
  );

  const handleEditPayment = (row) => {
    setSelected(row);
    setOpenEditModal(true);
  };

  const handleDeletePayment = (row) => {
    setSelected(row);
    setOpenDeleteModal(true);
  };

  return (
    <BodyWrapper>
      <Header
        title="Cash Payments"
        desc="Track cash in, cash out & CASH account balance"
      />

      <CashPaymentsSummaryHero
        summary={summary}
        loading={CashBookState.loading}
        accountName={accountName}
      />

      <div className="mb-5">
        <SegmentToggle
          options={TAB_OPTIONS.map((opt) => ({
            ...opt,
            activeClass:
              opt.value === "in"
                ? "bg-success text-white shadow-soft ring-1 ring-success/20"
                : "bg-rose-600 text-white shadow-sm ring-1 ring-rose-600/20",
          }))}
          value={activeTab}
          onChange={setActiveTab}
          fullWidth
        />
      </div>

      <div className="relative mb-5">
        <BsSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search description, party or invoice..."
          className="w-full pl-10 pr-4 py-3 rounded-xl border border-line bg-card text-sm shadow-soft focus:outline-none focus:border-main focus:ring-2 focus:ring-main/15 transition-all"
        />
      </div>

      {!CashBookState.loading && tabEntries.length > 0 && (
        <p className="text-xs text-muted font-medium mb-3">
          {activeTab === "in" ? "Cash In" : "Cash Out"} · Showing{" "}
          {tabEntries.length} transaction{tabEntries.length !== 1 ? "s" : ""}
          {" · "}
          <span
            className={activeTab === "in" ? "text-success" : "text-danger"}
          >
            Rs {tabSummary.total.toLocaleString("en-PK")}
          </span>
        </p>
      )}

      {CashBookState.loading ? (
        <FetchingLoading />
      ) : (
        <CashTxnCardList
          entries={tabEntries}
          searchText={searchText}
          emptyMessage={
            activeTab === "in"
              ? "No cash received in this date range."
              : "No cash paid out in this date range."
          }
          onEditPayment={handleEditPayment}
          onDeletePayment={handleDeletePayment}
        />
      )}

      {OpenEditModal && Selected && (
        <EditPaymentModal
          Open={OpenEditModal}
          setOpen={setOpenEditModal}
          paymentDetails={Selected}
          type={paymentTypeFromRow(Selected)}
          onSuccess={loadCashBook}
        />
      )}

      {OpenDeleteModal && Selected && (
        <DeleteModal
          Open={OpenDeleteModal}
          setOpen={setOpenDeleteModal}
          Loading={Loading}
          onSubmit={async () => {
            setLoading(true);
            try {
              const isCompany = Selected.source === "company_payment";
              const response = isCompany
                ? await DeleteSupplierPaymentApi(Selected._id)
                : await DeleteCustomerPaymentApi(Selected._id);
              if (response.data.success) {
                setOpenDeleteModal(false);
                SuccessToast(
                  isCompany
                    ? "Company payment deleted successfully"
                    : "Customer payment deleted successfully"
                );
                loadCashBook();
              } else {
                ErrorToast(response.data.error?.msg);
              }
            } catch (err) {
              ErrorToast(
                err?.response?.data?.error?.msg || "Failed to delete payment"
              );
            }
            setLoading(false);
          }}
        />
      )}
    </BodyWrapper>
  );
};

export default CashPayments;
